import MarkdownIt from 'markdown-it'
import { v4 as uuidv4 } from 'uuid'
import type { BotResponse } from './types'
import { AppMessage, BotMessage } from '@/composables/useSamGptCompletions/types'
import type { Customer } from '@/services/cdpService/types'
import { InvestmentProjectsSummaryResponse } from '@/server/api/buyer-front/types'
import { SAM_GPT_INTENT } from '@/utils/segment'

export const getTimeStamp = () => {
  const currentDate = new Date()
  return currentDate.toUTCString()
}

export const generateTurnId = (): string => uuidv4()

export const generateInteractionId = (): string => uuidv4()

export const getConversationId = (listingIds: string[], domainUserId: string) => {
  const listingIdsStr = listingIds.join('-')
  return `APP_CHAT/${listingIdsStr}/${domainUserId}`
}

export const generateMessageId = (domainUserId: string) => {
  return `${domainUserId}/${Date.now()}`
}

export const getMdRenderer = () => {
  const md = new MarkdownIt({
    breaks: true,
    html: true,
    linkify: true
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const defaultRender = md.renderer.rules.link_open || ((tokens, idx, options, env, self) => {
    return self.renderToken(tokens, idx, options)
  })

  md.renderer.rules.link_open = (tokens, idx: number, options, env, self) => {
    // Add 'target="_blank"' to the link
    const aIndex = tokens[idx].attrIndex('target')
    if (aIndex < 0) {
      tokens[idx].attrPush(['target', '_blank']) // Add new attribute 1
    } else {
      tokens[idx].attrs![aIndex][1] = '_blank' // Replace value if already set
    }

    // Ensure `rel="noopener noreferrer"` for security
    const relIndex = tokens[idx].attrIndex('rel')
    if (relIndex < 0) {
      tokens[idx].attrPush(['rel', 'noopener noreferrer'])
    } else {
      tokens[idx].attrs![relIndex][1] = 'noopener noreferrer'
    }

    // Use default renderer
    return defaultRender(tokens, idx, options, env, self)
  }

  return md
}

export const transformBoldToMarkdown = (message: string): string => {
  return message.replace(/(\*\*[^*]*\*\*)|(\*(.*?)\*)/g, (match, doubleAsterisks, _, singleAsterisksContent) => {
    if (doubleAsterisks) {
      return match
    }

    return `**${singleAsterisksContent}**`
  })
}

export const parseMessage = (content: string) => {
  const md = getMdRenderer() as MarkdownIt
  return md.render(content)
}

export const parseMessageToHtml = (message: AppMessage) => {
  let attachmentsLinks = ''

  if (message.attachments?.length) {
    attachmentsLinks = message.attachments.map(attachment => `adjunto(${attachment.media_url})`).join('<br>')
    return `${attachmentsLinks} <br> ${message.text}`
  }

  let recommendations = ''
  if (message.recommendations?.length) {
    recommendations = message.recommendations.map(rec => rec.name).join(', ')
    return `${message.text} <br> ${recommendations}`
  }

  let additionalComponents = ''
  if (message.additionalComponents?.length) {
    additionalComponents = message.additionalComponents.map(component => component.props.helperText).join('<br>')
    return `${message.text} <br> ${additionalComponents}`
  }

  return message.text
}

export const getSalesforceAuthorName = (author: string) => {
  if (author === 'bot') return 'Sam'

  return 'Usuario'
}

export const convertConversationToHtml = (conversationId: string, messages: AppMessage[]) => {
  const projectCode = conversationId.split('/')[1]
  const projectName = localStorage?.getItem(`sam_gpt_listing_${projectCode}`) || ''
  const content = `
    <section>
      <h1>
        <b>${projectName}[${projectCode}]</b>
      </h1>
      ${messages.map(message => `
      <div>
        <b>${getSalesforceAuthorName(message.author)} (${message.timestamp})</b>: ${parseMessageToHtml(message)}
      </div>
      <br>
      `).join('')}
    </section>
  `

  return content
}

const getAllPdpConversations = (domainUserId: string, conversationId: string, messages: AppMessage[]) => {
  const pattern = new RegExp(`^.*/${domainUserId}`)

  const conversations = {
    [conversationId]: messages
  }

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    const value = localStorage.getItem(key)

    if (pattern.test(key) && key !== conversationId) conversations[key] = JSON.parse(value)
  }
  return conversations
}

const getSalesforceFieldContent = (domainUserId: string, conversationId: string, messages: AppMessage[]) => {
  let html = ''

  const conversations = getAllPdpConversations(domainUserId, conversationId, messages)

  Object.keys(conversations).forEach((name:string) => (html += convertConversationToHtml(name, conversations[name])))

  return html
}

export const updateSalesforceLead = async (user: Customer, domainUserId: string, conversationId: string, messages: AppMessage[]) => {
  if (!user) return

  const html = getSalesforceFieldContent(domainUserId, conversationId, messages)
  await $fetch('/api/buyer-front/salesforce/lead', {
    method: 'PUT',
    body: {
      customers: [{
        summary_chat_web: html,
        user_id: user.id
      }]
    }
  })
}

export const extractListingIds = (messages) => {
  const relevantMessages = messages.filter(message => message.list_picker || message.carousel)

  const listingIds = relevantMessages
    .map(message => message.listing_ids || [])
    .flat()

  return listingIds
}

export const fetchRecommendations = async (listingIds: string, $sobreplanosDataService): Promise <InvestmentProjectsSummaryResponse> => {
  // @ts-ignore
  const filters = {
    code: listingIds
  }

  const pagination = {
    limit: 1000
  }

  const results = await $sobreplanosDataService?.getListings({ filters, pagination })
  return results
}

export const shouldStopAfterScheduleMessage = (response: BotResponse) => {
  return (
    response.output.stop_execution && response.output.messages.length === 0
  )
}

export const parseSamMessageAndGetAdditionalMessages = (messages: BotMessage[], appChatStore) => {
  const parsedMessages: BotMessage[] = []
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i]
    parsedMessages.push(message)
    message.intent = SAM_GPT_INTENT[message.intent]

    if (message.metadata?.available_turns_data?.length > 0) {
      message.text = ''
      message.additionalComponents = [{
        name: 'AppChatScheduleTurnSelection',
        props: {
          turns: message.metadata?.available_turns_data,
          helperText: message.metadata?.helper_text,
          showConsultTypeSelection: false
        }
      }]
    } else if (i === messages.length - 1 && appChatStore.endpointConfig.entryNameAlias.startsWith('PDP_CHAT_WEB') && appChatStore.endpointConfig.startFlow) {
      parsedMessages.push({
        text: '',
        additionalComponents: [
          {
            name: 'AppChatContactMethodSelection',
            props: {
              helperText: '',
              showConsultTypeSelection: true,
              is_in_person_visit_active: message.metadata?.is_in_person_visit_active
            }
          }
        ]
      })
    } else if (appChatStore.endpointConfig.entryNameAlias === 'PDP_REFER_AND_EARN' && appChatStore.endpointConfig.startFlow) {
      message.text = ''
      parsedMessages.push({
        text: '',
        additionalComponents: [
          {
            name: 'AppChatReferralSection',
            props: {
              helperText: 'Hola, soy Sam 👋🏻'
            }
          }
        ]
      })
    }
  }
  return parsedMessages
}
